import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Objective, ObjectiveStatus } from '../../types/pms';
import ProgressBar from './ProgressBar';
import './EmployeeSubmissionPanel.css';

interface EmployeeSubmissionPanelProps {
  /** All objectives for this employee in the selected review cycle */
  objectives: Objective[];
  /** Whether data is loading */
  loading?: boolean;
  /** Click handler for individual objective */
  onObjectiveClick?: (objective: Objective) => void;
  /** Approve a single objective */
  onApprove?: (objectiveId: string) => Promise<void>;
  /** Return a single objective with comments */
  onReturn?: (objectiveId: string, comments: string) => Promise<void>;
  /** Approve all submitted objectives in batch */
  onApproveAll?: (objectiveIds: string[]) => Promise<void>;
  /** Return all submitted objectives in batch */
  onReturnAll?: (objectiveIds: string[], comments: string) => Promise<void>;
}

function formatDate(dateString: string): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getStatusClass(status: ObjectiveStatus): string {
  switch (status) {
    case ObjectiveStatus.SUBMITTED:
      return 'pms-emp-submission__status--submitted';
    case ObjectiveStatus.APPROVED:
      return 'pms-emp-submission__status--approved';
    case ObjectiveStatus.RETURNED:
      return 'pms-emp-submission__status--returned';
    case ObjectiveStatus.IN_PROGRESS:
      return 'pms-emp-submission__status--inprogress';
    case ObjectiveStatus.COMPLETED:
      return 'pms-emp-submission__status--completed';
    default:
      return 'pms-emp-submission__status--draft';
  }
}

const EmployeeSubmissionPanel: React.FC<EmployeeSubmissionPanelProps> = ({
  objectives,
  loading = false,
  onObjectiveClick,
  onApprove,
  onReturn,
  onApproveAll,
  onReturnAll,
}) => {
  const [busy, setBusy] = useState(false);
  const [returnReasonId, setReturnReasonId] = useState<string | null>(null);
  const [returnComment, setReturnComment] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [processedObjectiveIds, setProcessedObjectiveIds] = useState<Set<string>>(new Set());

  const employee = objectives[0];

  useEffect(() => {
    setProcessedObjectiveIds((current) => {
      const objectiveIds = new Set(objectives.map((objective) => objective.id));
      const next = new Set(current);
      current.forEach((objectiveId) => {
        const objective = objectives.find((item) => item.id === objectiveId);
        if (!objectiveIds.has(objectiveId) || objective?.status !== ObjectiveStatus.SUBMITTED) next.delete(objectiveId);
      });
      const unchanged = next.size === current.size && Array.from(next).every((objectiveId) => current.has(objectiveId));
      return unchanged ? current : next;
    });
  }, [objectives]);

  const isActionableObjective = useCallback(
    (objective: Objective): boolean =>
      objective.status === ObjectiveStatus.SUBMITTED && !processedObjectiveIds.has(objective.id),
    [processedObjectiveIds]
  );

  const submittedObjectives = useMemo(
    () => objectives.filter(isActionableObjective),
    [objectives, isActionableObjective]
  );

  const totalWeightage = useMemo(
    () => objectives.reduce((sum, o) => sum + (o.weightage || 0), 0),
    [objectives]
  );

  const overallProgress = useMemo(() => {
    if (objectives.length === 0) return 0;
    const totalW = objectives.reduce((s, o) => s + (o.weightage || 0), 0);
    if (totalW === 0) return 0;
    return Math.round(
      objectives.reduce((s, o) => s + o.progressPercentage * (o.weightage || 0), 0) / totalW
    );
  }, [objectives]);

  const statusCounts = useMemo(() => ({
    approved: objectives.filter(o => o.status === ObjectiveStatus.APPROVED).length,
    pending: objectives.filter(o => o.status === ObjectiveStatus.SUBMITTED).length,
    returned: objectives.filter(o => o.status === ObjectiveStatus.RETURNED).length,
  }), [objectives]);

  const submittedDate = useMemo(() => {
    const submitted = objectives.filter(o => o.status === ObjectiveStatus.SUBMITTED);
    if (submitted.length === 0) return '';
    const earliest = submitted.reduce((earliest, o) =>
      new Date(o.updatedAt) < new Date(earliest.updatedAt) ? o : earliest
    );
    return earliest.updatedAt;
  }, [objectives]);

  if (loading) {
    return (
      <div className="pms-emp-submission pms-emp-submission--empty">
        <p>Loading...</p>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="pms-emp-submission pms-emp-submission--empty">
        <p>No objectives found for this employee.</p>
      </div>
    );
  }

  const handleApproveAll = async () => {
    if (!onApproveAll || submittedObjectives.length === 0) return;
    setBusy(true);
    try {
      const objectiveIds = submittedObjectives.map(o => o.id);
      await onApproveAll(objectiveIds);
      setProcessedObjectiveIds((current) => {
        const next = new Set(current);
        objectiveIds.forEach((objectiveId) => next.add(objectiveId));
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  const handleReturnAll = async () => {
    if (!onReturnAll || submittedObjectives.length === 0) return;
    const comment = window.prompt('Reason for returning these objectives?');
    if (!comment || !comment.trim()) return;
    setBusy(true);
    try {
      const objectiveIds = submittedObjectives.map(o => o.id);
      await onReturnAll(objectiveIds, comment.trim());
      setProcessedObjectiveIds((current) => {
        const next = new Set(current);
        objectiveIds.forEach((objectiveId) => next.add(objectiveId));
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  const handleApproveOne = async (objectiveId: string) => {
    if (!onApprove) return;
    setBusy(true);
    try {
      await onApprove(objectiveId);
      setProcessedObjectiveIds((current) => new Set(current).add(objectiveId));
    } finally {
      setBusy(false);
    }
  };

  const handleReturnOne = async (objectiveId: string) => {
    if (!onReturn) return;
    if (!returnComment.trim()) return;
    setBusy(true);
    try {
      await onReturn(objectiveId, returnComment.trim());
      setProcessedObjectiveIds((current) => new Set(current).add(objectiveId));
      setReturnReasonId(null);
      setReturnComment('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pms-emp-submission">
      <header className="pms-emp-submission__header">
        <button
          type="button"
          className="pms-emp-submission__header-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls="pms-emp-submission-objectives"
        >
          <span className="pms-emp-submission__avatar" aria-hidden="true">👤</span>
          <div className="pms-emp-submission__header-copy">
            <h2 className="pms-emp-submission__name">{employee.employeeName}</h2>
            <div className="pms-emp-submission__meta">
              <span>{objectives.length} Objectives</span>
              <span>{overallProgress}% Avg Progress</span>
              <span>{totalWeightage}% Total Weightage</span>
              {submittedDate && <span>Submitted {formatDate(submittedDate)}</span>}
            </div>
            <div className="pms-emp-submission__status-summary">
              <span>Approved {statusCounts.approved}</span>
              <span>Pending {statusCounts.pending}</span>
              <span>Returned {statusCounts.returned}</span>
            </div>
          </div>
          <span
            className={`pms-emp-submission__toggle-chevron ${expanded ? 'pms-emp-submission__toggle-chevron--open' : ''}`}
            aria-hidden="true"
          >
            ▾
          </span>
        </button>

        {submittedObjectives.length > 0 && (
          <div className="pms-emp-submission__batch-actions">
            <button
              type="button"
              className="pms-emp-submission__btn pms-emp-submission__btn--approve-all"
              onClick={handleApproveAll}
              disabled={busy}
              title={`Approve all ${submittedObjectives.length} submitted objectives`}
            >
              Approve All ({submittedObjectives.length})
            </button>
            <button
              type="button"
              className="pms-emp-submission__btn pms-emp-submission__btn--return-all"
              onClick={handleReturnAll}
              disabled={busy}
            >
              Return All
            </button>
          </div>
        )}
      </header>

      {expanded && (
        <div className="pms-emp-submission__objectives" id="pms-emp-submission-objectives">
          <div className="pms-emp-submission__table" role="table" aria-label={`Objectives submitted by ${employee.employeeName}`}>
            <div className="pms-emp-submission__table-head" role="row">
              <span role="columnheader">Objective Name</span>
              <span role="columnheader">Weightage</span>
              <span role="columnheader">Progress</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Submitted Date</span>
              <span role="columnheader">Actions</span>
            </div>

            {objectives.map((obj, index) => (
              <React.Fragment key={obj.id}>
                <div
                  className="pms-emp-submission__objective-row"
                  role="row"
                  onClick={() => onObjectiveClick?.(obj)}
                >
                  <div className="pms-emp-submission__objective-name" role="cell">
                    <span className="pms-emp-submission__objective-num">#{index + 1}</span>
                    <span className="pms-emp-submission__objective-title" title={obj.description || obj.title}>
                      {obj.title}
                    </span>
                  </div>
                  <div className="pms-emp-submission__weight" role="cell">{obj.weightage}%</div>
                  <div className="pms-emp-submission__row-progress" role="cell">
                    <ProgressBar value={obj.progressPercentage} size="sm" />
                  </div>
                  <div role="cell">
                    <span className={`pms-emp-submission__status ${getStatusClass(obj.status)}`}>
                      {obj.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="pms-emp-submission__submitted-date" role="cell">{formatDate(obj.updatedAt)}</div>
                  <div className="pms-emp-submission__objective-actions" role="cell" onClick={(event) => event.stopPropagation()}>
                    {isActionableObjective(obj) && onApprove && (
                      <button
                        type="button"
                        className="pms-emp-submission__btn pms-emp-submission__btn--approve"
                        onClick={() => handleApproveOne(obj.id)}
                        disabled={busy}
                      >
                        Approve
                      </button>
                    )}
                    {isActionableObjective(obj) && onReturn && (
                      <button
                        type="button"
                        className="pms-emp-submission__btn pms-emp-submission__btn--return"
                        onClick={() => setReturnReasonId(returnReasonId === obj.id ? null : obj.id)}
                        disabled={busy}
                      >
                        Return
                      </button>
                    )}
                    {onObjectiveClick && (
                      <button
                        type="button"
                        className="pms-emp-submission__btn pms-emp-submission__btn--view"
                        onClick={() => onObjectiveClick(obj)}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>

                {isActionableObjective(obj) && returnReasonId === obj.id && (
                  <div className="pms-emp-submission__return-form">
                    <textarea
                      className="pms-emp-submission__return-input"
                      placeholder="Reason for returning this objective..."
                      value={returnComment}
                      onChange={(e) => setReturnComment(e.target.value)}
                      rows={2}
                    />
                    <div className="pms-emp-submission__return-actions">
                      <button
                        type="button"
                        className="pms-emp-submission__btn pms-emp-submission__btn--cancel"
                        onClick={() => { setReturnReasonId(null); setReturnComment(''); }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="pms-emp-submission__btn pms-emp-submission__btn--return"
                        onClick={() => handleReturnOne(obj.id)}
                        disabled={busy || !returnComment.trim()}
                      >
                        Confirm Return
                      </button>
                    </div>
                  </div>
                )}

                {obj.managerComments && obj.status !== ObjectiveStatus.SUBMITTED && (
                  <div className="pms-emp-submission__manager-comments">
                    <strong>Manager comments:</strong> {obj.managerComments}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeSubmissionPanel;
